import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';

import { EmptyBreadcrumbsResolver } from '../breadcrumbs/breadcrumbs-empty.resolver';
import { GreyhoundsMeetingAllComponent } from '../racing/components/greyhounds/greyhounds-meeting/greyhounds-meeting-all.component';
import { GreyhoundsRaceDetailsComponent } from '../racing/components/greyhounds/greyhounds-race-details/greyhounds-race-details.component';
import { GreyhoundsRaceDetailsResolver } from '../racing/components/greyhounds/greyhounds-race-details/greyhounds-race-details.resolver';
import { SportRoute } from '../router/multi-route.model';
import { nameIdentifierMatcher } from '../router/name-identifier.matcher';
import { regexMatcher } from '../router/regex.matcher';
import { use } from '../router/router.resolver';
import { SitemapRoutingToken } from '../sitemap/sitemap-routing';
import { HorsesBaseComponent } from './common/horses-base.component';
import { AntepostFixtureListBreadcrumbsResolver } from './components/antepost-fixture-list/antepost-fixture-breadcrumb.resolver';
import { AntepostFixtureListComponent } from './components/antepost-fixture-list/antepost-fixture-list.component';
import { AntepostFixtureListResolver } from './components/antepost-fixture-list/antepost-fixture-list.resolver';
import { AntepostFixtureBreadcrumbsResolver } from './components/antepost-fixture/antepost-fixture-breadcrumb.resolver';
import { AntepostFixtureComponent } from './components/antepost-fixture/antepost-fixture.component';
import { AntepostFixtureResolver } from './components/antepost-fixture/antepost-fixture.resolver';
import { HorsesRaceSubNavigationService } from './components/base/horses-race-sub-navigation.service';
import { HorsesSubNavigationService } from './components/base/horses-sub-navigation.service';
import { GreyHoundsBaseComponent } from './components/greyhounds/base/greyhounds-base.component';
import { GreyhoundSubNavigationService } from './components/greyhounds/base/greyhounds-sub-navigation.service';
import { GreyhoundHomeComponent } from './components/greyhounds/gh-home/greyhound-home.component';
import { GreyhoundHomeResolver } from './components/greyhounds/gh-home/greyhound-home.resolver';
import { GreyhoundsMeetingAllResolver } from './components/greyhounds/greyhounds-meeting/greyhounds-meeting-all.resolver';
import { GreyhoundsDayOfRaceComponent } from './components/greyhounds/greyhounds-today/greyhounds-today.component';
import { GreyhoundsTodayResolver } from './components/greyhounds/greyhounds-today/greyhounds-today.resolver';
import { HorsesMeetingAllComponent } from './components/horses-meeting/horses-meeting-all.component';
import { HorsesMeetingAllResolver } from './components/horses-meeting/horses-meeting-all.resolver';
import { HorsesRaceDetailsComponent } from './components/horses-race-details/horses-race-details.component';
import { HorsesRaceDetailsResolver } from './components/horses-race-details/horses-race-details.resolver';
import { HorsesTodayComponent } from './components/horses-today/horses-today.component';
import { HorsesTodayResolver } from './components/horses-today/horses-today.resolver';
import { HorsesTomorrowComponent } from './components/horses-tomorrow/horses-tomorrow.component';
import { HorsesTomorrowResolver } from './components/horses-tomorrow/horses-tomorrow.resolver';
import { GreyhoundsRacingPagesGuard } from './greyhounds-racing-pages-guard';
import { RacingStylesResolver } from './racing-styles.resolver';
import { GreyhoundRaceDetailsBreadcrumbsResolver } from './services/greyhound-race-details-breadcrumbs.resolver';
import { HorsesBreadcrumbResolver } from './services/horses-breadcrumbs.resolver';
import { HorsesRaceDetailsBreadcrumbsResolver } from './services/horses-race-details-breadcrumbs.resolver';

const routes: SportRoute[] = [
    // sport antepost fixtures needed as we are mimicking sports
    // route for horses and we need to be more specific and we use
    // only the regexMatcher not to try to match regex multiple times
    {
        matcher: regexMatcher,
        data: {
            matcherPattern: '^((.*)-)?(\\d+)/{fixtures}/((.*)-)?(\\d:\\d+)$',
            matcherMode: 'full',
            matcherMapping: {
                sportName: 2,
                sport: 3,
                fixtureName: 5,
                fixture: 6,
            },
            htmlClass: 'horse-racing',
            breadcrumbs: AntepostFixtureBreadcrumbsResolver,
            tag: RouteTag.AntepostFixture,
        },
        component: AntepostFixtureComponent,
        resolve: use(AntepostFixtureResolver),
    },
    // golf specific routes
    {
        matcher: regexMatcher,
        data: {
            matcherPattern: `^((.*)-)?(${SportConstant.Golf})/{eachWay}$`,
            matcherMode: 'full',
            matcherMapping: {
                sportName: 2,
                sport: 3,
            },
            sport: SportConstant.Golf,
            breadcrumbs: AntepostFixtureListBreadcrumbsResolver,
            sitemap: SitemapRoutingToken.SitemapEmptyResolver,
            shouldKeepBreadcrumbs: true,
        },
        component: AntepostFixtureListComponent,
        resolve: use(AntepostFixtureListResolver),
    },
    // horse racing specific routes
    {
        matcher: regexMatcher,
        data: {
            matcherPattern: `^((.*)-)?(${SportConstant.Horses})$`,
            matcherMapping: {
                sportName: 2,
                sport: 3,
            },
            sport: SportConstant.Horses,
            breadcrumbs: HorsesBreadcrumbResolver,
            subNavigation: HorsesSubNavigationService,
        },
        component: HorsesBaseComponent,
        resolve: use(RacingStylesResolver), // Load racing styles upfront for all horses routes.
        children: [
            {
                path: '',
                redirectTo: '{today}',
                pathMatch: 'full',
            },
            {
                path: '{today}',
                component: HorsesTodayComponent,
                resolve: use(HorsesTodayResolver),
                data: {
                    tag: RouteTag.RacingToday,
                    sport: SportConstant.Horses,
                },
            },
            {
                path: '{tomorrow}',
                component: HorsesTomorrowComponent,
                resolve: use(HorsesTomorrowResolver),
                data: {
                    tag: RouteTag.RacingTomorrow,
                    sport: SportConstant.Horses,
                },
            },
            {
                path: '{future}',
                component: AntepostFixtureListComponent,
                data: { sport: SportConstant.Horses, tag: RouteTag.RacingFuture },
                resolve: use(AntepostFixtureListResolver),
            },
            {
                matcher: nameIdentifierMatcher,
                data: {
                    matcherParam: 'meetingId',
                    subNavigation: HorsesRaceSubNavigationService,
                    breadcrumbs: EmptyBreadcrumbsResolver,
                    sport: SportConstant.Horses,
                },
                children: [
                    {
                        path: '',
                        redirectTo: '{today}',
                        pathMatch: 'full',
                    },
                    {
                        matcher: regexMatcher,
                        component: HorsesMeetingAllComponent,
                        resolve: use(HorsesMeetingAllResolver),
                        data: {
                            matcherPattern: '^({today}|{tomorrow})$',
                            sport: SportConstant.Horses,
                            matcherMapping: {
                                filter: 1,
                            },
                            tag: RouteTag.HorseRaceDetails,
                            htmlClass: 'horse-race-details-page',
                            breadcrumbs: HorsesRaceDetailsBreadcrumbsResolver,
                        },
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        data: {
                            matcherParam: 'raceId',
                            sport: SportConstant.Horses,
                            subNavigation: HorsesRaceSubNavigationService,
                            tag: RouteTag.HorseRaceDetails,
                            htmlClass: 'horse-race-details-page',
                            breadcrumbs: HorsesRaceDetailsBreadcrumbsResolver,
                        },
                        component: HorsesRaceDetailsComponent,
                        resolve: use(HorsesRaceDetailsResolver),
                    },
                ],
            },
        ],
    },
    // greyhound racing specific routes
    {
        matcher: regexMatcher,
        data: {
            matcherPattern: `^((.*)-)?(${SportConstant.Greyhounds})$`,
            matcherMapping: {
                sportName: 2,
                sport: 3,
            },
            sport: SportConstant.Greyhounds,
            subNavigation: GreyhoundSubNavigationService,
        },
        component: GreyHoundsBaseComponent,
        resolve: use(RacingStylesResolver),
        children: [
            {
                path: '',
                redirectTo: '{nextRaces}',
                pathMatch: 'full',
            },
            {
                path: '{nextRaces}',
                component: GreyhoundHomeComponent,
                resolve: use(GreyhoundHomeResolver),
                data: { tag: RouteTag.RacingRaces, sport: SportConstant.Greyhounds },
                canActivate: [GreyhoundsRacingPagesGuard],
            },
            {
                path: '{today}',
                component: GreyhoundsDayOfRaceComponent,
                resolve: use(GreyhoundsTodayResolver),
                data: {
                    sport: SportConstant.Greyhounds,
                    tag: RouteTag.RacingToday,
                    dayOfRace: DayOfRaceDate.Today,
                    hideTimeFilter: true,
                },
                canActivate: [GreyhoundsRacingPagesGuard],
            },
            {
                path: '{tomorrow}',
                component: GreyhoundsDayOfRaceComponent,
                resolve: use(GreyhoundsTodayResolver),
                data: {
                    sport: SportConstant.Greyhounds,
                    tag: RouteTag.RacingTomorrow,
                    dayOfRace: DayOfRaceDate.Tomorrow,
                    hideTimeFilter: true,
                },
            },
            {
                path: '{future}',
                component: AntepostFixtureListComponent,
                data: { sport: SportConstant.Greyhounds, tag: RouteTag.RacingFuture },
                resolve: use(AntepostFixtureListResolver),
            },
            {
                matcher: nameIdentifierMatcher,
                data: {
                    matcherParam: 'meetingId',
                    sport: SportConstant.Greyhounds,
                    breadcrumbs: EmptyBreadcrumbsResolver,
                    subNavigation: HorsesRaceSubNavigationService,
                },
                children: [
                    {
                        path: '',
                        redirectTo: '{today}',
                        pathMatch: 'full',
                    },
                    {
                        matcher: regexMatcher,
                        component: GreyhoundsMeetingAllComponent,
                        resolve: use(GreyhoundsMeetingAllResolver),
                        data: {
                            matcherPattern: '^({today}|{tomorrow})$',
                            matcherMapping: {
                                filter: 1,
                            },
                            htmlClass: 'greyhound-race-details-page',
                            breadcrumbs: GreyhoundRaceDetailsBreadcrumbsResolver,
                        },
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        data: {
                            matcherParam: 'raceId',
                            sport: SportConstant.Greyhounds,
                            htmlClass: 'greyhound-race-details-page',
                            tag: RouteTag.GreyhoundRaceDetails,
                            breadcrumbs: GreyhoundRaceDetailsBreadcrumbsResolver,
                        },
                        component: GreyhoundsRaceDetailsComponent,
                        resolve: use(GreyhoundsRaceDetailsResolver),
                    },
                ],
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class RacingRoutingModule {}
